import type { OpenItemDto } from '@/types/openItem.types';

interface Props {
  item: Pick<OpenItemDto, 'origin' | 'externalDocumentNumber' | 'sourceDocumentNumber' | 'references'>;
  /** Show the source-system number too. Off by default — it is the least useful of the three to
   *  a human, and most grids are already tight. */
  showSourceNumber?: boolean;
}

/**
 * The references a human recognises, rendered the same way everywhere an open item appears.
 *
 * An open item carries three distinct identities and users need more than myLedger's own number:
 *   - documentNumber          — ours (BILL-…/INV-…), already shown by the caller
 *   - externalDocumentNumber  — the COUNTERPARTY's own number, the one printed on the statement
 *                               being reconciled. This is the one people actually search for, so
 *                               it leads here.
 *   - sourceDocumentNumber    — the originating system's (myPOS SINV-…)
 * plus 1:N references (GRN, PO, …), which are collapsed to a count because one invoice
 * legitimately matches several and a grid cannot afford to expand them.
 *
 * Renders nothing when there is nothing to add, so it is safe to drop into any row.
 */
export const DocumentReferences = ({ item, showSourceNumber = false }: Props) => {
  const refs = item.references ?? [];
  const hasExternal = !!item.externalDocumentNumber;
  const hasSource   = showSourceNumber && !!item.sourceDocumentNumber;

  if (!hasExternal && !hasSource && refs.length === 0) return null;

  // "GRN ×2 · PO ×1" — enough to tell the row apart without widening it.
  const grouped = refs.reduce<Record<string, number>>((acc, r) => {
    acc[r.refType] = (acc[r.refType] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="mt-0.5 space-y-0.5">
      {hasExternal && (
        <div
          className="text-[10px] text-slate-500 dark:text-slate-400 truncate"
          title={`Supplier/customer's own reference: ${item.externalDocumentNumber}`}
        >
          Ref <span className="font-mono text-slate-700 dark:text-slate-300">{item.externalDocumentNumber}</span>
        </div>
      )}
      {hasSource && (
        <div className="text-[10px] text-slate-400 truncate" title={`Source document: ${item.sourceDocumentNumber}`}>
          <span className="font-mono">{item.sourceDocumentNumber}</span>
        </div>
      )}
      {refs.length > 0 && (
        <div
          className="text-[10px] text-slate-400 truncate"
          title={refs.map(r => `${r.refType} ${r.refNumber}`).join('\n')}
        >
          {Object.entries(grouped).map(([t, n]) => `${t}${n > 1 ? ` ×${n}` : ''}`).join(' · ')}
        </div>
      )}
    </div>
  );
};
